import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, ViewChild, AfterViewInit, AfterViewChecked, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router, NavigationStart } from '@angular/router';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { map, takeUntil, switchMap, catchError, tap, finalize, filter } from 'rxjs/operators';
import { FeatureCollection, mask, featureCollection, Feature, bbox } from '@turf/turf';
import { MarketValueCardComponent, MarketValueData } from './components/market-value-card/market-value-card.component';
import { FSVCardComponent, FSVData } from './components/fsv-card/fsv-card.component';
import { ReliabilityCardComponent, ReliabilityData } from './components/reliability-card/reliability-card.component';
import { SubjectPropertyHeaderComponent, SubjectPropertyData } from './components/subject-property-header/subject-property-header.component';
import { CaseAssetInfoCardComponent, CaseData, AssetData } from './components/case-asset-info-card/case-asset-info-card.component';
import { DashboardDataService, DashboardData } from './services/dashboard-data.service';
import { DashboardToolbarComponent } from './components/dashboard-toolbar/dashboard-toolbar.component';
import { ComponentsModule } from '../../../shared/ui-kit/components/components.module';
import { DASHBOARD_TEMPLATES, DEFAULT_TEMPLATE_ID, WidgetId, DashboardTemplate } from './configs/dashboard-templates.config';
import { DashboardApiService } from '../../../core/services/dashboard-api.service';
import { ReportDetail } from '../../../core/models/dashboard.model';
import { PdfExportContextService } from '../../../core/services/pdf-export-context.service';
import { PdfExportDataService } from '../../../core/services/pdf-export-data.service';
import { GlobalLoaderService } from '../../../global-loader.service';
import { NzMessageService } from 'ng-zorro-antd/message';

// Declare window flag for PDF export
declare global {
  interface Window {
    PERARE_DASHBOARD_LOADED?: boolean;
  }
}

import { NgxMapboxGLModule, MapComponent } from 'ngx-mapbox-gl';
import { environment } from '../../../../environments/environment';
import * as mapboxgl from 'mapbox-gl';
import {
  dashError,
  dashGroupCollapsed,
  dashGroupEnd,
  dashLog,
  dashWarn,
  safeStringify,
  maskToken,
} from '../../../core/utils/dashboard-debug';
import { PolygonToLinesPipe } from '../../dp-map/utils/pipes/polygon-to-lines.pipe';
import { LinesToBearingLinesPipe } from '../../dp-map/utils/pipes/lines-to-bearing-lines.pipe';
import { PolygonToVertexLabelsArrayPipe } from '../../dp-map/utils/pipes/polygon-to-vertex-labels-array.pipe';
import { PolygonToPointsPipe } from '../../dp-map/utils/pipes/polygon-to-points.pipe';
import { PolygonToSimplifyPolygonPipe } from '../../dp-map/utils/pipes/polygon-to-simplify-polygon.pipe';
import { PerareFeature } from '../../../shared/services/api/listing-api.types';
import { DetailedPlotSettings } from '../../../shared/services/api/boards-api.service';
import { XPlaceholderRowComponent } from '../../../shared/x-kit/components/x-placeholder-row/x-placeholder-row.component';
import { XPlaceholderFixedLoaderComponent } from '../../../shared/x-kit/components/x-placeholder-row/x-placeholder-fixed-loader/x-placeholder-fixed-loader.component';
import { XPlaceholderFlexibleLoaderComponent } from '../../../shared/x-kit/components/x-placeholder-row/x-placeholder-flexible-loader/x-placeholder-flexible-loader.component';

type ComparableProperty = {
  address: string;
  area: number;
  totalPrice: string;
  pricePerSqm: string;
  distance: number;
  date: string;
  similarity: number;
  adjustedPrice: string;
  selected: boolean;
  coordinates?: [number, number];
};

type MapPoint = {
  type: 'subject' | 'comparable';
  top: number;
  left: number;
};

type AuditInfo = {
  valuationId: string;
  createdAt: string;
  model: string;
};

const DEFAULT_MAP_CENTER: [number, number] = [106.827153, -6.175392];

const BACKGROUND_POLYGON_COORDINATES: [number, number][] = [
  [106.828842, -6.175908],
  [106.829308, -6.175078],
  [106.828197, -6.174612],
  [106.827598, -6.175336],
  [106.828842, -6.175908]
];

const BACKGROUND_POLYGON_GEOJSON = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [BACKGROUND_POLYGON_COORDINATES]
      }
    }
  ]
} as const;

@Component({
  selector: 'app-project-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MarketValueCardComponent,
    FSVCardComponent,
    ReliabilityCardComponent,
    SubjectPropertyHeaderComponent,
    DashboardToolbarComponent,
    NgxMapboxGLModule,
    ComponentsModule,
    PolygonToLinesPipe,
    LinesToBearingLinesPipe,
    PolygonToVertexLabelsArrayPipe,
    PolygonToPointsPipe,
    PolygonToSimplifyPolygonPipe,
    XPlaceholderRowComponent,
    XPlaceholderFixedLoaderComponent,
    XPlaceholderFlexibleLoaderComponent,
    CaseAssetInfoCardComponent,
  ],
  templateUrl: './project-dashboard.component.html',
  styleUrl: './project-dashboard.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectDashboardComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  @ViewChild(MapComponent) mapComponent?: MapComponent;
  @ViewChild('printMapComponent') printMapComponent?: MapComponent;
  private mapLoaded = false;
  private mapTilesLoaded = false;
  private printMapSubscribed = false;

  boardId$: Observable<string>;
  private destroy$ = new Subject<void>();
  private readonly enableMapMarkers = true;
  private readonly DEFAULT_PLOT_UUID = '0199aa4f-5492-792a-b4eb-50f0c2223bd3';
  private markers: mapboxgl.Marker[] = [];
  private comparableMarkersMap = new Map<ComparableProperty, mapboxgl.Marker>();
  private printMarkers: mapboxgl.Marker[] = [];
  private printComparableMarkersMap = new Map<ComparableProperty, mapboxgl.Marker>();
  private selectedComparableMarker: mapboxgl.Marker | null = null;
  private mapSubscribed = false;
  private readonly defaultMapCenter = DEFAULT_MAP_CENTER;

  // Window size tracking for responsive map positioning
  windowWidth: number = typeof window !== 'undefined' ? window.innerWidth : 1920;
  windowHeight: number = typeof window !== 'undefined' ? window.innerHeight : 1080;

  @HostListener('window:resize', ['$event'])
  onResize(event?: Event) {
    if (typeof window !== 'undefined') {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
      // Clear cache when window size changes
      this.cachedFitBoundsOptions = null;
    }
    this.cdr.markForCheck();
  }

  // Template configuration
  currentTemplateId: string = DEFAULT_TEMPLATE_ID;
  currentTemplate: DashboardTemplate = DASHBOARD_TEMPLATES[DEFAULT_TEMPLATE_ID];

  // Состояние загрузки данных
  isLoading = false;
  hasData = false;
  errorMessage: string | null = null;

  // PDF export mode flag
  isPdfMode = false;
  // Map-only public mode flag (show only map + polygon)
  isMapOnlyMode = false;
  showCaseAssetInfo = false; // Блок Case/Asset Info скрыт по умолчанию

  // Данные для компонентов (будут обновляться из API)
  marketValueData: MarketValueData | null = null;
  fsvData: FSVData | null = null;
  reliabilityData: ReliabilityData | null = null;
  subjectPropertyData: SubjectPropertyData | null = null;

  // Mock data for Case/Asset Info Card
  caseData: CaseData = {
    caseId: 'VAL-2025-11-12345',
    date: '17 Nov 2025, 14:32',
    autoUpdate: 'Off',
    avmModel: 'v1.4.2'
  };

  assetData: AssetData = {
    plotUuid: undefined,
    center: undefined,
  };
  comparableProperties: ComparableProperty[] = [];
  comparablePropertiesWarning: { message: string; suggestions?: string[] } | null = null;
  selectedComparable: ComparableProperty | null = null;
  mapPoints: MapPoint[] = [];
  auditInfo: AuditInfo | null = null;
  currentReport: ReportDetail | null = null;
  readonly timeWindowOptions = ['Last 90 days', 'Last 6 months'];
  readonly distanceOptions = ['Within 1 km', 'Within 3 km'];
  private pdfExportMessageRef: { messageId: string } | null = null;
  activeTimeWindow = 'Last 90 days';
  activeDistance = 'Within 1 km';

  getTranslatedTimeWindow(): string {
    const keyMap: { [key: string]: string } = {
      'Last 90 days': 'appreciation_rate_time_window_last_90_days',
      'Last 6 months': 'appreciation_rate_time_window_last_6_months'
    };
    const key = keyMap[this.activeTimeWindow] || this.activeTimeWindow;
    return this.translate.instant(key);
  }

  getTranslatedDistance(): string {
    const keyMap: { [key: string]: string } = {
      'Within 1 km': 'appreciation_rate_distance_within_1_km',
      'Within 3 km': 'appreciation_rate_distance_within_3_km'
    };
    const key = keyMap[this.activeDistance] || this.activeDistance;
    return this.translate.instant(key);
  }
  readonly mapboxToken = environment.mapboxToken;
  private polygonCoordinatesFromReport: [number, number][] | null = null;
  isochroneData: FeatureCollection | null = null;
  isochroneMaskData: FeatureCollection | null = null;

  // Map display settings
  detailedSettings: DetailedPlotSettings = {
    isShowDetailedDimensions: true,
    isShowNorthAngle: true,
    isShowBoundaries: true,
    parcelFollowingEnabled: false,
  };

  // Cached bounds to prevent recalculation on every change detection
  private cachedPolygonBounds: [[number, number], [number, number]] | undefined = undefined;
  private cachedFitBoundsOptions: any = null;
  private initialFitBoundsApplied = false; // Track if initial fitBounds was applied

  // Get polygon GeoJSON from report data or fallback to default
  get subjectPolygonGeoJson() {
    const coordinates = this.polygonCoordinatesFromReport || BACKGROUND_POLYGON_COORDINATES;
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates]
          }
        }
      ]
    } as const;
  }

  // Convert polygon to PerareFeature format for pipes
  get subjectPolygonFeatures(): PerareFeature[] {
    const coordinates = this.polygonCoordinatesFromReport || BACKGROUND_POLYGON_COORDINATES;
    if (!coordinates || coordinates.length === 0) {
      return [];
    }
    return [
      {
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates]
        },
        properties: {}
      }
    ];
  }

  // Show north angle (from settings)
  get showNorthAngle(): boolean {
    return this.detailedSettings.isShowNorthAngle;
  }

  // Handle distance filter change
  // Note: isochrone is now included in the report response, so we don't need to reload it
  onDistanceChange(newDistance: string): void {
    if (this.activeDistance === newDistance) {
      return; // No change
    }

    this.activeDistance = newDistance;
    dashLog('distance filter changed', { newDistance });
    // Isochrone is now included in the report response, so no need to reload
    this.cdr.markForCheck();
  }

  // Calculate bounds from polygon and all markers to use with fitBounds
  // TEMPORARY: Centering by isochrone instead of polygon
  get polygonBounds(): [[number, number], [number, number]] | undefined {
    // Return cached value if available to prevent recalculation
    if (this.cachedPolygonBounds !== undefined) {
      return this.cachedPolygonBounds;
    }

    return this.calculatePolygonBounds();
  }

  private calculatePolygonBounds(): [[number, number], [number, number]] | undefined {
    // TEMPORARY: Use isochrone bounds if available
    if (!this.isMapOnlyMode && this.isochroneData && this.isochroneData.features.length > 0) {
      try {
        const bounds = bbox(this.isochroneData);
        // bbox returns [minX, minY, maxX, maxY]
        return [
          [bounds[0], bounds[1]], // Southwest corner [minLng, minLat]
          [bounds[2], bounds[3]]  // Northeast corner [maxLng, maxLat]
        ];
      } catch (error) {
        dashWarn('failed to calculate isochrone bounds', { error });
      }
    }

    // Fallback to original polygon bounds calculation
    const coordinates = this.polygonCoordinatesFromReport || BACKGROUND_POLYGON_COORDINATES;
    const allLngs: number[] = [];
    const allLats: number[] = [];

    // Add polygon coordinates
    if (coordinates && coordinates.length > 0) {
      coordinates.forEach(coord => {
        allLngs.push(coord[0]);
        allLats.push(coord[1]);
      });
    }

    // Map-only mode: bounds should be based ONLY on polygon coordinates
    if (this.isMapOnlyMode) {
      if (allLngs.length === 0 || allLats.length === 0) {
        return undefined;
      }

      const bounds: [[number, number], [number, number]] = [
        [Math.min(...allLngs), Math.min(...allLats)],  // Southwest corner
        [Math.max(...allLngs), Math.max(...allLats)]   // Northeast corner
      ];
      this.cachedPolygonBounds = bounds;
      return bounds;
    }

    // Helper function to convert coordinates to [lng, lat] format
    const convertToLngLat = (coords: [number, number]): [number, number] | null => {
      const [first, second] = coords;
      const isFirstLat = first >= -11 && first <= 5;
      const isFirstLon = first >= 95 && first <= 141;
      const isSecondLat = second >= -11 && second <= 5;
      const isSecondLon = second >= 95 && second <= 141;

      if (isFirstLat && isSecondLon) {
        return [second, first] as [number, number]; // [lat, lon] -> [lng, lat]
      } else if (isFirstLon && isSecondLat) {
        return [first, second] as [number, number]; // Already [lng, lat]
      } else {
        // Fallback: assume [lat, lon]
        return [second, first] as [number, number];
      }
    };

    // Add subject property coordinates
    if (this.subjectPropertyData?.coordinates && this.subjectPropertyData.coordinates.length === 2) {
      const lngLat = convertToLngLat(this.subjectPropertyData.coordinates as [number, number]);
      if (lngLat && lngLat[0] >= 95 && lngLat[0] <= 141 && lngLat[1] >= -11 && lngLat[1] <= 5) {
        allLngs.push(lngLat[0]);
        allLats.push(lngLat[1]);
      }
    }

    // Add all comparable property coordinates
    this.comparableProperties.forEach(comp => {
      if (comp.coordinates && comp.coordinates.length === 2) {
        const lngLat = convertToLngLat(comp.coordinates);
        if (lngLat && lngLat[0] >= 95 && lngLat[0] <= 141 && lngLat[1] >= -11 && lngLat[1] <= 5) {
          allLngs.push(lngLat[0]);
          allLats.push(lngLat[1]);
        }
      }
    });

    if (allLngs.length === 0 || allLats.length === 0) {
      return undefined;
    }

    const bounds: [[number, number], [number, number]] = [
      [Math.min(...allLngs), Math.min(...allLats)],  // Southwest corner
      [Math.max(...allLngs), Math.max(...allLats)]   // Northeast corner
    ];
    this.cachedPolygonBounds = bounds;
    return bounds;
  }

  // Padding to center polygon - responsive based on screen width
  get fitBoundsOptions() {
    // Return cached value if window size hasn't changed
    if (this.cachedFitBoundsOptions &&
        this.cachedFitBoundsOptions._windowWidth === this.windowWidth &&
        this.cachedFitBoundsOptions._windowHeight === this.windowHeight &&
        this.cachedFitBoundsOptions._isMapOnlyMode === this.isMapOnlyMode) {
      return this.cachedFitBoundsOptions;
    }

    return this.calculateFitBoundsOptions();
  }

  // FitBounds options for print mode - minimal padding for A3 format
  get printModeFitBoundsOptions() {
    return {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10
      },
      maxZoom: 15, // Немного уменьшен zoom для лучшего fit в A3
      duration: 0,
    };
  }

  private calculateFitBoundsOptions() {
    if (this.isMapOnlyMode) {
      // Map-only: make polygon larger in viewport by using smaller padding
      // and allowing a higher maxZoom.
      const padding = 80;
      const options = {
        padding: {
          left: padding,
          right: padding,
          top: padding,
          bottom: padding
        },
        maxZoom: 18,
        duration: 0,
        _windowWidth: this.windowWidth,
        _windowHeight: this.windowHeight,
        _isMapOnlyMode: this.isMapOnlyMode,
      };
      this.cachedFitBoundsOptions = options;
      return options;
    }

    if (this.windowWidth < 1400) {
      // Mobile: visible map area is fixed at 340px (from CSS padding-top on dashboard-content)
      // Toolbar is ~60px, so visible area starts at ~60px and ends at ~400px (60 + 340)

      const toolbarHeight = 60;
      const visibleMapArea = 340; // Fixed from CSS

      // To center polygon in visible 340px area:
      const topPadding = toolbarHeight;
      const bottomPadding = this.windowHeight - toolbarHeight - visibleMapArea;

      // Horizontal: center with margin
      const horizontalPadding = Math.max(this.windowWidth * 0.1, 40);

      const options = {
        padding: {
          left: horizontalPadding,
          right: horizontalPadding,
          top: topPadding,
          bottom: bottomPadding
        },
        maxZoom: 16,
        duration: 0,
        _windowWidth: this.windowWidth,
        _windowHeight: this.windowHeight,
        _isMapOnlyMode: this.isMapOnlyMode,
      };
      this.cachedFitBoundsOptions = options;
      return options;
    } else {
      // Large screen: show polygon on the right
      const options = {
        padding: {
          left: 1200,  // ~1128px widgets + gaps + extra margin
          top: 80,
          right: 80,
          bottom: 300
        },
        maxZoom: 16,
        duration: 0,
        _windowWidth: this.windowWidth,
        _windowHeight: this.windowHeight,
        _isMapOnlyMode: this.isMapOnlyMode,
      };
      this.cachedFitBoundsOptions = options;
      return options;
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dashboardDataService: DashboardDataService,
    private dashboardApiService: DashboardApiService,
    private pdfContext: PdfExportContextService,
    private pdfExportDataService: PdfExportDataService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    public globalLoader: GlobalLoaderService,
    private message: NzMessageService
  ) {
    dashLog('constructor', {
      url: typeof window !== 'undefined' ? window.location?.href : undefined,
    });
    this.boardId$ = this.route.params.pipe(map(params => params['boardId']));
  }

  ngOnInit(): void {
    dashGroupCollapsed('ngOnInit', {
      url: typeof window !== 'undefined' ? window.location?.href : undefined,
    });

    // Subscribe to router navigation events to hide PDF export snackbar on navigation
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.pdfExportMessageRef) {
        this.message.remove(this.pdfExportMessageRef.messageId);
        this.pdfExportMessageRef = null;
      }
    });

    const exportMode = this.route.snapshot?.data?.['exportMode'] as 'pdf' | 'map' | undefined;
    const dataSource = this.route.snapshot?.data?.['dataSource'] as 'session' | undefined;
    this.isPdfMode = exportMode === 'pdf';
    this.isMapOnlyMode = exportMode === 'map';

    // Check for print mode from URL parameter
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const isPrintMode = urlParams.get('print') === 'true';
      if (isPrintMode) {
        this.isPdfMode = true;
        dashLog('print mode detected from URL parameter', {});
      }
    }

    // Ensure layout-related caches are recalculated for the selected mode
    this.cachedPolygonBounds = undefined;
    this.cachedFitBoundsOptions = null;

    // Check if this is data-only export mode (sessionId in route params, data from sessionStorage)
    if (dataSource === 'session') {
      this.route.params.pipe(
        takeUntil(this.destroy$),
        map(params => params['sessionId'])
      ).subscribe(sessionId => {
        if (sessionId) {
          dashLog('data-only export mode detected', {
            exportMode,
            sessionId,
            dataSource
          });
          this.isPdfMode = true;
          this.pdfContext.setPdfMode(true);
          this.pdfContext.setDataOnlyMode(true);
          this.loadReportFromSessionStorage(sessionId);
          return;
        }
      });
      return;
    }

    // Check if this is public export mode (token in route params)
    this.route.params.pipe(
      takeUntil(this.destroy$),
      map(params => ({
        token: params['token'],
        projectId: params['projectId'],
        dashboardId: params['dashboardId'],
        reportId: params['reportId']
      }))
    ).subscribe(({ token, projectId, dashboardId, reportId }) => {
      if (token && projectId && dashboardId && reportId) {
        dashLog('public export mode detected', {
          exportMode,
          token: maskToken(token),
          projectId,
          dashboardId,
          reportId
        });
        this.isPdfMode = true;
        this.pdfContext.setPdfMode(true);
        this.loadReportForPdfExport(token, projectId, dashboardId, reportId);
        return;
      }

      // Normal mode - get projectId and dashboardId from route
      combineLatest([
        this.route.params,
        this.route.parent?.params || of({} as any)
      ]).pipe(
        takeUntil(this.destroy$),
        map(([params, parentParams]) => {
          const dashboardId = params['boardId'];
          const projectId = (parentParams as any)?.['project_id'] || params['project_id'];
          return { projectId, dashboardId };
        })
      ).subscribe(({ projectId, dashboardId }) => {
        dashLog('route params resolved', { projectId, dashboardId });

        if (!projectId || !dashboardId) {
          dashWarn('missing projectId or dashboardId', { projectId, dashboardId });
          this.errorMessage = 'Dashboard configuration not found';
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        // Check for initial plot UUID from navigation state (when created from rooms page)
        const navigationState = history.state;
        const initialPlotUuid = navigationState?.plotUuid;
        dashLog('initial plotUuid from navigation state', { initialPlotUuid });

        // Initialize dashboard: check reports and load data
        this.initializeDashboard(projectId, dashboardId, initialPlotUuid);
      });
    });
  }

  /**
   * Load report for PDF export using token from URL
   */
  private loadReportForPdfExport(token: string, projectId: string, dashboardId: string, reportId: string): void {
    dashLog('loadReportForPdfExport', { projectId, dashboardId, reportId, token: maskToken(token) });
    this.isLoading = true;
    this.cdr.markForCheck();

    this.dashboardApiService.getReportByToken(token, projectId, dashboardId, reportId).pipe(
      takeUntil(this.destroy$),
      map((report) => {
        if (!report) {
          return null;
        }
        //note: [SHR] current data sometimes doesnt appears in export
        this.currentReport = report;
        this.updateCaseDataFromReport(report);
        // Convert report payload into dashboard view-model data
        return this.dashboardDataService.buildDashboardDataFromReport(report);
      })
    ).subscribe({
      next: (data) => {
        if (!data) {
          this.isLoading = false;
          this.hasData = false;
          this.cdr.markForCheck();
          return;
        }

        dashLog('dashboard data loaded for PDF export (summary)', {
          uuid: (data as any)?.uuid,
          marketValueRaw: (data as any)?.marketValue?.valueRaw,
          fsvAmountRaw: (data as any)?.fsv?.amountRaw,
          comparables: (data as any)?.reliabilityMeta?.comparablesCount,
          coords: (data as any)?.subjectProperty?.coordinates,
          hasReliability: !!(data as any)?.reliability,
          reliabilityScore: (data as any)?.reliability?.score?.value,
        });
        this.updateComponentsWithRealData(data);
        this.isLoading = false;
        this.hasData = true;
        this.errorMessage = null;
        this.cdr.markForCheck();
      },
      error: (error) => {
        dashError('error loading report for PDF export', {
          status: (error as any)?.status,
          message: (error as any)?.message,
          response: safeStringify((error as any)?.error),
        });
        this.isLoading = false;
        this.hasData = false;
        this.errorMessage = error?.message || 'Failed to load report for PDF export';
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load report for PDF export from sessionStorage (data-only mode, no API calls)
   */
  private loadReportFromSessionStorage(sessionId: string): void {
    dashLog('loadReportFromSessionStorage', { sessionId });
    this.isLoading = true;
    this.cdr.markForCheck();

    const reportData = this.pdfExportDataService.getReportData(sessionId);

    if (!reportData) {
      dashError('report data not found in sessionStorage', { sessionId });
      this.isLoading = false;
      this.hasData = false;
      this.errorMessage = 'Report data not found. Please try exporting again.';
      this.cdr.markForCheck();
      return;
    }

    dashLog('report data loaded from sessionStorage', {
      hasData: !!reportData,
      reportId: (reportData as any)?.id,
      plotUuid: (reportData as any)?.plotUuid,
    });

    try {
      //note: [SHR] current data sometimes doesnt appears in export
      this.currentReport = reportData;
      this.updateCaseDataFromReport(reportData);
      // Convert report payload into dashboard view-model data
      const data = this.dashboardDataService.buildDashboardDataFromReport(reportData);

      if (!data) {
        this.isLoading = false;
        this.hasData = false;
        this.cdr.markForCheck();
        return;
      }

      dashLog('dashboard data loaded from sessionStorage (summary)', {
        uuid: (data as any)?.uuid,
        marketValueRaw: (data as any)?.marketValue?.valueRaw,
        fsvAmountRaw: (data as any)?.fsv?.amountRaw,
        comparables: (data as any)?.reliabilityMeta?.comparablesCount,
        coords: (data as any)?.subjectProperty?.coordinates,
        hasReliability: !!(data as any)?.reliability,
        reliabilityScore: (data as any)?.reliability?.score?.value,
      });

          this.updateComponentsWithRealData(data);
          this.isLoading = false;
          this.hasData = true;
          this.errorMessage = null;

          if (typeof window !== 'undefined') {
            if (this.isPdfMode) {
              setTimeout(() => {
                window.PERARE_DASHBOARD_LOADED = true;
                dashLog('PERARE_DASHBOARD_LOADED flag set to true (PDF mode)');
              }, 500);
            } else {
              window.PERARE_DASHBOARD_LOADED = true;
              dashLog('PERARE_DASHBOARD_LOADED flag set to true (Standard mode)');
            }
          }

          this.cdr.markForCheck();
        } catch (error) {
          dashError('error building dashboard data from sessionStorage report', {
        error: (error as any)?.message,
        sessionId,
      });
      this.isLoading = false;
      this.hasData = false;
      this.errorMessage = 'Failed to process report data. Please try exporting again.';
      this.cdr.markForCheck();
    }
  }

  /**
   * Initialize dashboard: check reports, generate if needed, load data
   */
  private initializeDashboard(projectId: string, dashboardId: string, initialPlotUuid?: string): void {
    dashLog('initializeDashboard', { projectId, dashboardId, initialPlotUuid });
    this.isLoading = true;
    this.cdr.markForCheck();

    // Check if reports exist
    this.dashboardApiService.getReports(projectId, dashboardId).pipe(
      takeUntil(this.destroy$),
      switchMap(reports => {
        dashLog('reports loaded', {
          count: Array.isArray(reports) ? reports.length : 0,
        });

        if (!reports || reports.length === 0) {
          // No reports: generate first report
          if (initialPlotUuid) {
            dashLog('no reports → generating first report', { plotUuid: initialPlotUuid });
            return this.generateFirstReport(projectId, dashboardId, initialPlotUuid);
          } else {
            // No reports and no plot UUID - show empty state
            dashWarn('no reports and no initial plotUuid provided', {});

            this.isLoading = false;
            this.hasData = false;
            this.errorMessage = null; // Show "Ready for Property Valuation" state
            this.cdr.markForCheck();
            return of(null);
          }
        } else {
          // Reports exist: sort by createdAt descending (newest first)
          const sortedReports = [...reports].sort((a, b) => {
            const dateA = new Date((a as any).createdAt || 0).getTime();
            const dateB = new Date((b as any).createdAt || 0).getTime();
            return dateB - dateA; // Sort descending (newest first)
          });

          let selectedReport: any;

          if (initialPlotUuid) {
            // If initialPlotUuid is provided, prioritize matching reports
            const successReports = sortedReports.filter((r: any) => r?.status === 'success');
            const plotSuccessMatch = successReports.find((r: any) => r?.plotUuid === initialPlotUuid);
            const plotAnyMatch = sortedReports.find((r: any) => r?.plotUuid === initialPlotUuid);
            selectedReport = plotSuccessMatch || plotAnyMatch || successReports[0] || sortedReports[0];
          } else {
            // No initialPlotUuid: always use the latest created report (first in sorted list)
            selectedReport = sortedReports[0];
          }

          dashLog('selected report', {
            id: selectedReport?.id,
            status: selectedReport?.status,
            plotUuid: selectedReport?.plotUuid,
            createdAt: selectedReport?.createdAt,
            widgetCount: selectedReport?.widgetCount,
            hasInitialPlotUuid: !!initialPlotUuid,
            isLatestReport: !initialPlotUuid || sortedReports[0]?.id === selectedReport?.id,
          });

          if (selectedReport?.id) {
            // Load full report details
            return this.dashboardApiService.getReport(projectId, dashboardId, selectedReport.id);
          } else {
            dashWarn('selected report missing id', { selectedReport });
            this.errorMessage = 'Report data incomplete';
            this.isLoading = false;
            this.cdr.markForCheck();
            return of(null);
          }
        }
      }),
      map((report) => {
        if (!report) {
          return null;
        }
        // Сохраняем информацию о репорте для использования в caseData
        this.currentReport = report;
        // Обновляем caseData с реальными данными из репорта
        this.updateCaseDataFromReport(report);
        // Convert report payload into dashboard view-model data
        return this.dashboardDataService.buildDashboardDataFromReport(report);
      })
    ).subscribe({
      next: (data) => {
        if (!data) {
          this.isLoading = false;
          this.hasData = false;
          this.cdr.markForCheck();
          return;
        }

        dashLog('dashboard data loaded (summary)', {
          uuid: (data as any)?.uuid,
          marketValueRaw: (data as any)?.marketValue?.valueRaw,
          fsvAmountRaw: (data as any)?.fsv?.amountRaw,
          comparables: (data as any)?.reliabilityMeta?.comparablesCount,
          coords: (data as any)?.subjectProperty?.coordinates,
          hasReliability: !!(data as any)?.reliability,
          reliabilityScore: (data as any)?.reliability?.score?.value,
        });
        this.updateComponentsWithRealData(data);
        this.isLoading = false;
        this.hasData = true;
        this.errorMessage = null;

        // Reset initial fitBounds flag when new data is loaded
        this.initialFitBoundsApplied = false;

        if (this.isPdfMode) {
        } else if (typeof window !== 'undefined') {
          window.PERARE_DASHBOARD_LOADED = true;
        }

        this.cdr.markForCheck();
      },
      error: (error) => {
        dashError('error in initialization flow', {
          status: (error as any)?.status,
          message: (error as any)?.message,
          response: safeStringify((error as any)?.error),
        });
        this.isLoading = false;
        this.hasData = false;
        this.errorMessage = error?.message || 'Failed to initialize dashboard';
        this.cdr.markForCheck();
      }
    });
  }

  public onToggleCaseAssetInfo(): void {
    this.showCaseAssetInfo = !this.showCaseAssetInfo;
    this.cdr.markForCheck();
  }

  public onSettingsChange(settings: DetailedPlotSettings): void {
    this.detailedSettings = { ...settings };
    this.cdr.markForCheck();

    // Handle parcel following - auto-center map on polygon
    if (settings.parcelFollowingEnabled && this.polygonBounds && this.mapComponent?.mapInstance) {
      const bounds = this.polygonBounds;
      this.mapComponent.mapInstance.fitBounds(bounds, {
        padding: 50,
        duration: 500,
      });
    }
  }

  public onReportChanged(report: any): void {
    dashLog('onReportChanged (toolbar)', {
      hasReport: !!report,
      id: (report as any)?.id,
      status: (report as any)?.status,
      plotUuid: (report as any)?.plotUuid ?? (report as any)?.data?.plotUuid,
    });

    try {
      const data = this.dashboardDataService.buildDashboardDataFromReport(report);
      this.updateComponentsWithRealData(data);
      this.hasData = true;
      this.errorMessage = null;
    } catch (e: any) {
      dashError('failed to apply report', { message: e?.message });
      this.hasData = false;
      this.errorMessage = e?.message || 'Failed to apply report';
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Generate first report for the dashboard
   */
  private generateFirstReport(projectId: string, dashboardId: string, plotUuid: string): Observable<any> {
    dashLog('generateFirstReport', { projectId, dashboardId });
    return this.dashboardApiService.generateReport(projectId, dashboardId).pipe(
      tap({
        next: (res) =>
          dashLog('generateFirstReport OK', {
            id: (res as any)?.id,
            status: (res as any)?.status,
          }),
        error: (err) =>
          dashError('generateFirstReport FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
            response: safeStringify((err as any)?.error),
          }),
      })
    );
  }

  ngAfterViewInit(): void {
    dashLog('ngAfterViewInit');
    // Map might not be visible yet if hasData is false
    // We'll check after each view check
  }

  ngAfterViewChecked(): void {
    // Check if map component became available and subscribe to it only once
    if (this.mapComponent && !this.mapSubscribed) {
      dashLog('map component detected → subscribing to mapLoad and mapCreate');
      this.mapSubscribed = true;

      this.mapComponent.mapCreate.pipe(takeUntil(this.destroy$)).subscribe((map) => {
        console.log('[DashboardMap] Map created');
        this.checkMapCapabilities(map);
      });

      this.mapComponent.mapLoad.pipe(takeUntil(this.destroy$)).subscribe(() => {
        dashLog('map loaded → updating markers');
        this.updateMapMarkers();
        this.onMapLoad();
      });
    }

    // Check if PDF/print mode map component became available and subscribe to it only once
    if (this.isPdfMode && this.printMapComponent && !this.printMapSubscribed) {
      dashLog('print mode map component detected → subscribing to mapLoad');
      this.printMapSubscribed = true;

      this.printMapComponent.mapLoad.pipe(takeUntil(this.destroy$)).subscribe(() => {
        dashLog('print mode map loaded');
        this.onPrintMapLoad();
      });
    }
  }

  private onMapLoad(): void {
    this.mapLoaded = true;
    const map = this.mapComponent?.mapInstance;
    if (!map) return;

    this.waitForMapFullyLoaded(map);
  }

  // Handle print mode map load
  onPrintMapLoad(): void {
    dashLog('print mode map loaded');
    const map = this.printMapComponent?.mapInstance;
    if (!map) {
      dashWarn('print mode map instance not available');
      return;
    }

    // Wait for map to be fully loaded, then apply fitBounds
    if (map.isStyleLoaded()) {
      this.applyPrintMapFitBounds(map);
    } else {
      map.once('styledata', () => {
        this.applyPrintMapFitBounds(map);
      });
    }

    this.updatePrintMapMarkers();
  }

  private applyPrintMapFitBounds(map: mapboxgl.Map): void {
    if (!this.polygonBounds) {
      dashWarn('print mode: no polygon bounds available');
      return;
    }

    const bounds = this.polygonBounds;
    const boundsObj = new mapboxgl.LngLatBounds(
      [bounds[0][0], bounds[0][1]],
      [bounds[1][0], bounds[1][1]]
    );

    map.fitBounds(boundsObj, this.printModeFitBoundsOptions);
    dashLog('print mode: fitBounds applied', { bounds });
  }

  private checkMapCapabilities(map: any): void {
    try {
      const canvas = map.getCanvas();
      const gl = canvas?.getContext('webgl') || canvas?.getContext('webgl2') || canvas?.getContext('experimental-webgl');

      if (!gl) {
        console.error('[DashboardMap] WebGL context not available');
        return;
      }
    } catch (error) {
      console.error('[DashboardMap] Error checking WebGL capabilities:', error);
    }
  }

  private waitForMapFullyLoaded(map: any): void {
    let attempts = 0;
    const maxAttempts = 50; //note: [SHR] pay attention here

    const checkInterval = setInterval(() => {
      attempts++;

      if (map.isStyleLoaded() && map.areTilesLoaded()) {
        console.log('[DashboardMap] Map fully loaded with tiles after', attempts * 100, 'ms');
        this.mapTilesLoaded = true;
        clearInterval(checkInterval);
        this.setMapLoadedFlag();
      } else if (attempts >= maxAttempts) {
        console.warn('[DashboardMap] Map loading timeout after 5s');
        clearInterval(checkInterval);
        this.setMapLoadedFlag();
      }
    }, 100);
  }

  private setMapLoadedFlag(): void {
    if (typeof window !== 'undefined') {
      console.log('[DashboardMap] Setting PERARE_DASHBOARD_LOADED = true');
      window.PERARE_DASHBOARD_LOADED = true;
    }
  }

  ngOnDestroy(): void {
    // Remove PDF export snackbar if it's still showing
    if (this.pdfExportMessageRef) {
      this.message.remove(this.pdfExportMessageRef.messageId);
      this.pdfExportMessageRef = null;
    }
    this.pdfContext.setPdfMode(false);
    this.pdfContext.setDataOnlyMode(false);
    this.clearMarkers();
    this.clearPrintMarkers();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Test helper: Check if PDF export data is available in sessionStorage
   * Can be called from browser console for debugging
   */
  public testCheckPdfExportData(): void {
    const keys = Object.keys(sessionStorage).filter(k => k.startsWith('pdf-export-data-'));
    console.log('📊 PDF Export Data Check:');
    console.log(`Found ${keys.length} session(s) in sessionStorage`);

    keys.forEach(key => {
      const sessionId = key.replace('pdf-export-data-', '');
      const data = this.pdfExportDataService.getReportData(sessionId);
      if (data) {
        console.log(`\n✅ Session: ${sessionId}`);
        console.log('  - Report ID:', (data as any)?.id);
        console.log('  - Plot UUID:', (data as any)?.plotUuid);
        console.log('  - Status:', (data as any)?.status);
        console.log('  - Has widgets:', !!(data as any)?.data?.widgets);
        if ((data as any)?.data?.widgets) {
          console.log('  - Widget count:', Object.keys((data as any).data.widgets).length);
        }
      } else {
        console.log(`\n❌ Session: ${sessionId} - data not found`);
      }
    });

    if (keys.length === 0) {
      console.log('No PDF export data found. Export a dashboard first.');
    }
  }

  /**
   * Export dashboard to PDF
   * Calls backend API /api/pdf-export/export which uses Playwright to generate PDF
   */
  public exportToPdf(): void {
    dashGroupCollapsed('PDF export: exportToPdf called');

    // Get projectId and dashboardId from route
    const projectId = this.route.snapshot.paramMap.get('project_id') ||
                     this.route.parent?.snapshot.paramMap.get('project_id') ||
                     this.route.parent?.parent?.snapshot.paramMap.get('project_id');
    const dashboardId = this.route.snapshot.paramMap.get('boardId') ||
                       this.route.snapshot.paramMap.get('dashboardId');

    if (!projectId || !dashboardId) {
      dashError('PDF export error: projectId or dashboardId is missing', {
        projectId,
        dashboardId,
      });
      dashGroupEnd();
      return;
    }

    dashLog('PDF export: showing snackbar notification');
    const messageRef = this.message.loading(
      this.translate.instant('dashboard_pdf_export_generating'),
      { nzDuration: 0 }
    );
    this.pdfExportMessageRef = messageRef;

    dashLog('PDF export: calling API', {
      projectId,
      dashboardId,
      apiUrl: '/api/pdf-export/export',
      printMode: true,
    });

    // Always use print mode for PDF export
    this.dashboardApiService
      .exportPdf(projectId, dashboardId, true)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          dashGroupEnd();
        })
      )
      .subscribe({
        next: (blob: Blob) => {
          dashLog('PDF export: API call successful', {
            blobSize: blob.size,
            blobType: blob.type,
          });

          // Remove snackbar when download starts
          this.message.remove(messageRef.messageId);

          if (!blob || blob.size === 0) {
            dashError('PDF export error: Received empty blob from API', {
              blobSize: blob?.size,
              blobType: blob?.type,
            });
            return;
          }

          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.href = url;
          link.download = `dashboard-${Date.now()}.pdf`;

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          dashLog('PDF export downloaded successfully', {
            blobSize: blob.size,
            blobType: blob.type,
          });
        },
        error: (error) => {
          // Remove snackbar on error
          this.message.remove(messageRef.messageId);
          this.pdfExportMessageRef = null;
          console.error('PDF export error:', error);
          dashError('PDF export failed', {
            status: (error as any)?.status,
            message: (error as any)?.message,
            url: (error as any)?.url,
            name: (error as any)?.name,
            stack: (error as any)?.stack,
          });
        },
      });
  }


  /**
   * Загрузка данных дашборда по UUID участка
   */
  private loadDashboardData(uuid: string): void {
    this.isLoading = true;
    this.hasData = false;
    this.errorMessage = null;
    this.cdr.markForCheck();

    this.dashboardDataService.loadDashboardData(uuid).subscribe({
      next: (data) => {
        dashLog('loadDashboardData OK (summary)', {
          uuid: (data as any)?.uuid,
          marketValueRaw: (data as any)?.marketValue?.valueRaw,
        });
        this.updateComponentsWithRealData(data);
        this.isLoading = false;
        this.hasData = true;
        this.errorMessage = null;

        // Auto-center map if parcel following is enabled
        if (this.detailedSettings.parcelFollowingEnabled && this.polygonBounds && this.mapComponent?.mapInstance) {
          setTimeout(() => {
            const bounds = this.polygonBounds;
            if (bounds) {
              this.mapComponent?.mapInstance?.fitBounds(bounds, {
                padding: 50,
                duration: 500,
              });
            }
          }, 100);
        }

        this.cdr.markForCheck();
      },
      error: (error) => {
        dashError('loadDashboardData FAILED', {
          status: (error as any)?.status,
          message: (error as any)?.message,
          response: safeStringify((error as any)?.error),
        });
        this.isLoading = false;
        this.hasData = false;
        this.errorMessage = error?.message || 'Backend returned an error';
        this.cdr.markForCheck();
        // Можно показать уведомление об ошибке
      }
    });
  }

  /**
   * Обновление caseData из репорта
   */
  private updateCaseDataFromReport(report: ReportDetail): void {
    if (!report) {
      return;
    }

    // Форматируем дату из ISO строки в формат "17 Nov 2025, 14:32"
    const formatDate = (isoString: string): string => {
      try {
        const date = new Date(isoString);
        const day = date.getDate();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day} ${month} ${year}, ${hours}:${minutes}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    };

    // Используем ID репорта как Case ID
    const caseId = report.id || '';
    const formattedDate = report.createdAt ? formatDate(report.createdAt) : '';

    this.caseData = {
      ...this.caseData,
      caseId: caseId,
      date: formattedDate,
    };

    // Обновляем assetData из report.data.data
    const reportData = (report as any)?.data?.data || (report as any)?.data;
    if (reportData) {
      const plotUuid = reportData.plotUuid || (report as any)?.plotUuid;
      const center = reportData.center || reportData.coordinates;
      const nib = reportData.nib;
      const nomor = reportData.nomor;
      const address = reportData.address;

      if (plotUuid || center || nib || nomor || address) {
        this.assetData = {
          ...this.assetData,
          plotUuid: plotUuid || this.assetData.plotUuid,
          center: center || this.assetData.center,
          nib: nib || this.assetData.nib,
          nomor: nomor || this.assetData.nomor,
          address: address || this.assetData.address,
        };

        dashLog('assetData updated from report', {
          plotUuid,
          center,
          nib,
          nomor,
          address,
        });
      }
    }

    dashLog('caseData updated from report', {
      caseId,
      date: formattedDate,
      originalCreatedAt: report.createdAt,
    });
  }

  /**
   * Обновление компонентов реальными данными
   */
  private updateComponentsWithRealData(data: DashboardData): void {
    console.log('🔄 [RELIABILITY] updateComponentsWithRealData START', { data: data ? 'exists' : 'null' });
    console.log('🔄 [RELIABILITY] updateComponentsWithRealData CALLED', {
      hasReliability: !!data.reliability,
      reliabilityScore: data.reliability?.score?.value,
      reliabilityLevel: data.reliability?.score?.level,
      factorsCount: data.reliability?.factors?.length,
      reliabilityData: data.reliability
    });
    dashLog('updateComponentsWithRealData START', {
      timestamp: new Date().toISOString(),
      hasData: !!data,
      uuid: (data as any)?.uuid,
      hasMarket: !!(data as any)?.marketValue,
      hasFsv: !!(data as any)?.fsv,
      hasSubject: !!(data as any)?.subjectProperty,
      hasReliability: !!data.reliability,
      reliabilityScore: data.reliability?.score?.value,
      reliabilityLevel: data.reliability?.score?.level,
      factorsCount: data.reliability?.factors?.length,
      hasComparables: !!(data.comparables && data.comparables.length > 0),
      comparablesCount: data.comparables?.length || 0,
      hasPolygonCoordinates: !!data.polygonCoordinates,
      polygonCoordinatesCount: data.polygonCoordinates?.length || 0
    });

    // Сохраняем координаты полигона из репорта
    if (data.polygonCoordinates && data.polygonCoordinates.length > 0) {
      this.polygonCoordinatesFromReport = data.polygonCoordinates;
      dashLog('polygon coordinates updated from report', {
        count: data.polygonCoordinates.length
      });
    } else {
      this.polygonCoordinatesFromReport = null;
      dashLog('no polygon coordinates in report, using default');
    }

    // Используем isochrone из данных репорта (если есть)
    this.isochroneData = data.isochrone || null;

    // Clear cached bounds when data changes
    this.cachedPolygonBounds = undefined;

    // Создаем инвертированную маску для затемнения области вне изохроны
    if (this.isochroneData && this.isochroneData.features.length > 0) {
      try {
        // mask() возвращает Feature<Polygon>, нужно обернуть в FeatureCollection
        // Также нужно убедиться, что features имеют тип Polygon или MultiPolygon
        const maskFeature = mask(this.isochroneData as FeatureCollection<any>);
        this.isochroneMaskData = featureCollection([maskFeature]);
        dashLog('isochrone mask created', {
          hasMask: !!this.isochroneMaskData,
          maskType: this.isochroneMaskData?.type
        });
      } catch (error) {
        dashWarn('failed to create isochrone mask', { error });
        this.isochroneMaskData = null;
      }
    } else {
      this.isochroneMaskData = null;
    }

    dashLog('isochrone data from report', {
      hasIsochrone: !!this.isochroneData,
      featuresCount: this.isochroneData?.features?.length || 0,
      hasMask: !!this.isochroneMaskData
    });

    // Обновляем данные для Market Value
    this.marketValueData = {
      value: data.marketValue.value,
      valueRaw: data.marketValue.valueRaw,
      currency: data.marketValue.currency,
      landArea: data.marketValue.landArea,
      landAreaRaw: data.marketValue.landAreaRaw,
      perSqmExact: data.marketValue.perSqmExact,
      perSqmExactRaw: data.marketValue.perSqmExactRaw,
      valueHigh: data.marketValue.valueHigh,
      valueHighRaw: data.marketValue.valueHighRaw,
      valueLow: data.marketValue.valueLow,
      valueLowRaw: data.marketValue.valueLowRaw,
      perSqmHigh: data.marketValue.perSqmHigh,
      perSqmHighRaw: data.marketValue.perSqmHighRaw,
      perSqmLow: data.marketValue.perSqmLow,
      perSqmLowRaw: data.marketValue.perSqmLowRaw,
      perSqmHigh95: data.marketValue.perSqmHigh95,
      perSqmHigh95Raw: data.marketValue.perSqmHigh95Raw,
      perSqmLow95: data.marketValue.perSqmLow95,
      perSqmLow95Raw: data.marketValue.perSqmLow95Raw,
      volatility: data.marketValue.volatility,
      history: data.marketValue.history
    };

    // Обновляем данные для Subject Property
    this.subjectPropertyData = {
      title: data.subjectProperty.title,
      area: data.subjectProperty.area,
      category: data.subjectProperty.category,
      address: data.subjectProperty.address,
      coordinates: data.subjectProperty.coordinates
    };

    const hasAssetFields = !!(
      this.assetData.plotUuid ||
      this.assetData.nib ||
      this.assetData.nomor ||
      this.assetData.address ||
      this.assetData.center
    );
    if (!hasAssetFields) {
      const fallbackAddress = data.subjectProperty.address;
      const fallbackCenter = data.subjectProperty.coordinates;
      if (fallbackAddress || fallbackCenter) {
        this.assetData = {
          ...this.assetData,
          address: fallbackAddress || this.assetData.address,
          center: fallbackCenter || this.assetData.center,
        };
        dashLog('assetData filled from subjectProperty', {
          address: fallbackAddress,
          center: fallbackCenter,
        });
      }
    }

    // Обновляем данные для FSV
    this.fsvData = {
      amount: data.fsv.amount,
      amountRaw: data.fsv.amountRaw,
      haircutPercentage: data.fsv.haircutPercentage,
      haircutDescription: data.fsv.haircutDescription,
      currency: data.fsv.currency,
      policyLabel: data.fsv.policyLabel,
      mainText: data.fsv.mainText,
      policyName: data.fsv.policyName
    };

    // Use reliability data from API
    if (data.reliability) {
      this.reliabilityData = data.reliability;
      dashLog('using API reliability data', {
        score: this.reliabilityData.score.value,
        level: this.reliabilityData.score.level
      });
    } else {
      // No reliability data available - don't show widget
      this.reliabilityData = null;
      dashWarn('reliability data not available in API response', {});
    }

    // Use real comparables data if available, otherwise fall back to mock
    if (data.comparables && data.comparables.length > 0) {
      dashLog('using real comparables from API', { count: data.comparables.length });
      // Filter to show only comparables that are in selected_comparisons
      const selectedComparables = data.comparables.filter(comp => comp.selected === true);
      dashLog('filtered comparables to selected only', {
        totalCount: data.comparables.length,
        selectedCount: selectedComparables.length
      });
      this.comparableProperties = selectedComparables.map(comp => ({
        address: comp.address,
        area: comp.area,
        totalPrice: comp.totalPrice,
        pricePerSqm: comp.pricePerSqm,
        distance: comp.distance,
        date: comp.date,
        similarity: comp.similarity,
        adjustedPrice: comp.adjustedPrice,
        selected: comp.selected,
        coordinates: comp.coordinates
      }));
    } else {
      dashWarn('no comparables in data', {});
      this.comparableProperties = [];
    }
    dashLog('component view-models updated', {
      marketValue: this.marketValueData?.valueRaw,
      fsv: this.fsvData?.amountRaw,
      subjectCoords: this.subjectPropertyData?.coordinates,
      comparables: (data as any)?.reliabilityMeta?.comparablesCount,
      compsListCount: this.comparableProperties?.length ?? 0,
    });

    // Update map markers after data is loaded
    this.updateMapMarkers();

    if (this.isPdfMode) {
    } else if (typeof window !== 'undefined') {
      window.PERARE_DASHBOARD_LOADED = true;
      dashLog('PERARE_DASHBOARD_LOADED flag set to true (not in PDF mode)');
    }
  }

  /**
   * Wait for map to be fully loaded and all layers rendered (for PDF export)
   * Returns a promise that resolves when map is idle and all tiles are loaded
   */
  private waitForMapReady(): Promise<void> {
    dashLog('waitForMapReady: starting to wait for map to be ready');
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const timeout = setTimeout(() => {
        const elapsed = Date.now() - startTime;
        dashWarn('waitForMapReady: timeout after 15 seconds', {
          elapsedMs: elapsed,
          hasMapComponent: !!this.mapComponent,
          hasMapInstance: !!this.mapComponent?.mapInstance,
          mapLoaded: this.mapComponent?.mapInstance?.loaded() ?? false,
          mapStyleLoaded: this.mapComponent?.mapInstance?.isStyleLoaded() ?? false,
          mapIsMoving: this.mapComponent?.mapInstance?.isMoving() ?? false,
        });
        reject(new Error('Map ready timeout after 15 seconds'));
      }, 15000);

      const checkMap = () => {
        const map = this.mapComponent?.mapInstance;
        if (!map) {
          dashLog('waitForMapReady: map instance not found, retrying...', {
            hasMapComponent: !!this.mapComponent,
            hasMapInstance: false,
          });
          // Map not initialized yet, wait a bit and retry
          setTimeout(checkMap, 200);
          return;
        }

        dashLog('waitForMapReady: map instance found', {
          mapLoaded: map.loaded(),
          mapStyleLoaded: map.isStyleLoaded(),
          mapIsMoving: map.isMoving(),
        });

        // Check if map is loaded
        if (!map.loaded()) {
          dashLog('waitForMapReady: waiting for map load event');
          // Wait for map to load
          map.once('load', () => {
            const elapsed = Date.now() - startTime;
            dashLog('waitForMapReady: map load event received', {
              elapsedMs: elapsed,
              mapStyleLoaded: map.isStyleLoaded(),
              mapIsMoving: map.isMoving(),
            });
            // After load, wait for idle (all tiles rendered)
            if (map.isStyleLoaded() && !map.isMoving()) {
              // Map is already idle
              dashLog('waitForMapReady: map is already idle after load, waiting 500ms');
              clearTimeout(timeout);
              // Additional delay to ensure all layers are rendered
              setTimeout(() => {
                const totalElapsed = Date.now() - startTime;
                dashLog('waitForMapReady: map ready (idle after load)', {
                  totalElapsedMs: totalElapsed,
                });
                resolve();
              }, 500);
            } else {
              dashLog('waitForMapReady: waiting for map idle event after load');
              map.once('idle', () => {
                const elapsed = Date.now() - startTime;
                dashLog('waitForMapReady: map idle event received after load', {
                  elapsedMs: elapsed,
                });
                clearTimeout(timeout);
                // Additional delay to ensure all layers are rendered
                setTimeout(() => {
                  const totalElapsed = Date.now() - startTime;
                  dashLog('waitForMapReady: map ready (idle after load + delay)', {
                    totalElapsedMs: totalElapsed,
                  });
                  resolve();
                }, 500);
              });
            }
          });
        } else {
          dashLog('waitForMapReady: map is already loaded, checking idle state');
          // Map is loaded, wait for idle
          if (map.isStyleLoaded() && !map.isMoving()) {
            // Map is already idle
            dashLog('waitForMapReady: map is already idle, waiting 500ms');
            clearTimeout(timeout);
            setTimeout(() => {
              const totalElapsed = Date.now() - startTime;
              dashLog('waitForMapReady: map ready (already idle)', {
                totalElapsedMs: totalElapsed,
              });
              resolve();
            }, 500);
          } else {
            dashLog('waitForMapReady: waiting for map idle event', {
              mapStyleLoaded: map.isStyleLoaded(),
              mapIsMoving: map.isMoving(),
            });
            map.once('idle', () => {
              const elapsed = Date.now() - startTime;
              dashLog('waitForMapReady: map idle event received', {
                elapsedMs: elapsed,
              });
              clearTimeout(timeout);
              setTimeout(() => {
                const totalElapsed = Date.now() - startTime;
                dashLog('waitForMapReady: map ready (idle + delay)', {
                  totalElapsedMs: totalElapsed,
                });
                resolve();
              }, 500);
            });
          }
        }
      };

      // Start checking
      checkMap();
    });
  }

  trackComparable(_index: number, item: ComparableProperty): string {
    return `${item.address}-${item.date}`;
  }

  /**
   * Highlight comparable property marker without moving the map
   */
  flyToComparable(comp: ComparableProperty): void {
    if (!comp.coordinates || comp.coordinates.length !== 2) {
      dashWarn('comparable has no coordinates', { comp });
      return;
    }

    const marker = this.comparableMarkersMap.get(comp);

    if (!marker) {
      dashWarn('marker not found for comparable', { comp });
      return;
    }

    dashLog('selecting comparable (no map movement)', {
      address: comp.address,
      coordinates: comp.coordinates
    });

    // Set selection state
    this.selectedComparableMarker = marker;
    this.selectedComparable = comp;

    // Update all markers to reflect selection state (will highlight the selected one with pulse animation)
    this.updateMarkerStyles();

    this.cdr.markForCheck();
  }

  /**
   * Update marker styles based on selection with pulse animation
   */
  private updateMarkerStyles(): void {
    this.comparableMarkersMap.forEach((marker, comp) => {
      const element = marker.getElement();
      if (element) {
        const isSelected = this.selectedComparable === comp;
        if (isSelected) {
          // Selected marker: darker blue, bigger, with pulse animation
          element.style.backgroundColor = '#155DFC'; // info-strong color (darker blue)
          element.style.width = '16px';
          element.style.height = '16px';
          element.style.zIndex = '1000';
          element.style.animation = 'markerPulse 1.5s ease-in-out infinite';
          // Transition only for visual properties, NOT for transform (position)
          element.style.transition = 'background-color 0.3s ease, width 0.3s ease, height 0.3s ease';
        } else {
          // Normal marker: dark grey, reset animation
          element.style.backgroundColor = '#2D3748'; // dark grey
          element.style.width = '12px';
          element.style.height = '12px';
          element.style.zIndex = '';
          element.style.animation = 'none';
          // Transition only for visual properties, NOT for transform (position)
          element.style.transition = 'background-color 0.3s ease, width 0.3s ease, height 0.3s ease';
        }
      }
    });

    // Also update subject marker if needed (for consistency)
    this.markers.forEach(marker => {
      const element = marker.getElement();
      if (element && !this.comparableMarkersMap.has(this.selectedComparable as any)) {
        // Subject marker stays normal
        element.style.backgroundColor = '#2D3748';
        element.style.width = '12px';
        element.style.height = '12px';
        element.style.animation = 'none';
      }
    });
  }


  copyValuationId(value: string): void {
    if (typeof navigator === 'undefined' || !navigator?.clipboard) {
      return;
    }
    navigator.clipboard.writeText(value).catch(error => {
      dashWarn('failed to copy valuation id', {
        message: (error as any)?.message,
      });
    });
  }

  get mapCenter(): [number, number] {
    // Return base coordinates - fitBounds with padding will handle centering
    return this.subjectPropertyData?.coordinates || this.defaultMapCenter;
  }

  /**
   * Check if a widget should be displayed based on current template
   */
  shouldShowWidget(widgetId: WidgetId): boolean {
    return this.currentTemplate.widgets.includes(widgetId);
  }

  private clearMarkers(): void {
    this.markers.forEach(marker => marker.remove());
    this.markers = [];
    this.comparableMarkersMap.clear();
    this.selectedComparableMarker = null;
    this.selectedComparable = null;
  }

  private clearPrintMarkers(): void {
    this.printMarkers.forEach(marker => marker.remove());
    this.printMarkers = [];
    this.printComparableMarkersMap.clear();
  }

  /**
   * Create a custom marker element (dot)
   */
  private createDotMarker(color: string, size: number = 8, cursor: 'pointer' | 'default' = 'pointer'): HTMLElement {
    const el = document.createElement('div');
    el.className = 'custom-map-marker';
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.borderRadius = '50%';
    el.style.backgroundColor = color;
    el.style.border = `2px solid #FFFFFF`;
    el.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
    el.style.cursor = cursor;
    // Optimize for smooth marker movement during map drag
    el.style.willChange = 'transform';
    // Transition only for visual properties (size, color), NOT for transform (position)
    // Transform is handled by Mapbox and should update instantly without transition
    el.style.transition = 'background-color 0.3s ease, width 0.3s ease, height 0.3s ease';
    return el;
  }

  private applyInitialFitBoundsIfNeeded(): void {
    if (this.initialFitBoundsApplied) {
      return;
    }
    const map = this.mapComponent?.mapInstance;
    const bounds = this.polygonBounds;
    if (!map || !bounds) {
      return;
    }

    const boundsObj = new mapboxgl.LngLatBounds(
      [bounds[0][0], bounds[0][1]],
      [bounds[1][0], bounds[1][1]]
    );

    const apply = () => {
      map.fitBounds(boundsObj, this.fitBoundsOptions);
      this.initialFitBoundsApplied = true;
      dashLog('initial map bounds applied');
    };

    if (map.isStyleLoaded()) {
      apply();
    } else {
      map.once('styledata', apply);
    }
  }

  private updateMapMarkers(): void {
    // Even when markers are disabled (e.g. map-only mode), we still want the map to fit the polygon once.
    this.applyInitialFitBoundsIfNeeded();

    if (this.isMapOnlyMode || !this.enableMapMarkers) {
      return;
    }
    dashLog('updateMapMarkers', {
      hasMapComponent: !!this.mapComponent,
      hasMapInstance: !!this.mapComponent?.mapInstance,
      hasSubjectCoords: !!this.subjectPropertyData?.coordinates,
      comparableCount: this.comparableProperties?.length ?? 0,
    });

    if (!this.mapComponent?.mapInstance) {
      dashWarn('map instance not ready yet', {});
      return;
    }

    // Clear existing markers
    this.clearMarkers();

    const map = this.mapComponent.mapInstance;
    dashLog('map markers input (summary)', {
      subjectCoords: this.subjectPropertyData?.coordinates,
      comparableWithCoords: this.comparableProperties?.filter((c) => !!c.coordinates).length ?? 0,
    });

    // Add subject property marker as a custom dot
    if (this.subjectPropertyData?.coordinates && this.subjectPropertyData.coordinates.length === 2) {
      const [first, second] = this.subjectPropertyData.coordinates;

      // Coordinates from API are typically [lat, lon], need to convert to [lng, lat] for Mapbox
      // For Indonesia: lat is approximately -11 to -5, lon is approximately 95 to 141
      let lngLat: [number, number];

      const isFirstLat = first >= -11 && first <= 5;
      const isFirstLon = first >= 95 && first <= 141;
      const isSecondLat = second >= -11 && second <= 5;
      const isSecondLon = second >= 95 && second <= 141;

      if (isFirstLat && isSecondLon) {
        // [lat, lon] format - swap to [lng, lat]
        lngLat = [second, first] as [number, number];
        dashLog('creating subject marker [lat,lon]->[lng,lat]', {
          original: this.subjectPropertyData.coordinates,
          converted: lngLat
        });
      } else if (isFirstLon && isSecondLat) {
        // [lon, lat] format - already correct
        lngLat = [first, second] as [number, number];
        dashLog('creating subject marker [lng,lat] (already correct)', {
          coordinates: lngLat
        });
      } else {
        // Fallback: assume [lat, lon] format
        lngLat = [second, first] as [number, number];
        dashWarn('subject coordinates validation failed, using fallback conversion', {
          original: this.subjectPropertyData.coordinates,
          converted: lngLat
        });
      }

      // Create custom dot marker - using dark grey (#2D3748) with white border
      const markerElement = this.createDotMarker('#2D3748', 12);
      const marker = new mapboxgl.Marker(markerElement)
        .setLngLat(lngLat)
        .addTo(map);

      this.markers.push(marker);
      dashLog('subject marker added');
    } else {
      dashWarn('no subject coordinates', {});
    }

    // Add comparable property markers as custom dots
    this.comparableProperties.forEach((comp, index) => {
      if (comp.coordinates && comp.coordinates.length === 2) {
        const [first, second] = comp.coordinates;

        // Coordinates are stored as [lat, lon] from API
        // Mapbox expects [lng, lat], so we need to swap them
        // For Indonesia: lat is approximately -11 to -5, lon is approximately 95 to 141
        // Validate and convert to [lng, lat] format
        let lngLat: [number, number];

        // Check if coordinates are in valid Indonesia range
        // If first value is in lat range (-11 to 5) and second is in lon range (95 to 141), it's [lat, lon]
        // If first value is in lon range (95 to 141) and second is in lat range (-11 to 5), it's [lon, lat]
        const isFirstLat = first >= -11 && first <= 5;
        const isFirstLon = first >= 95 && first <= 141;
        const isSecondLat = second >= -11 && second <= 5;
        const isSecondLon = second >= 95 && second <= 141;

        if (isFirstLat && isSecondLon) {
          // [lat, lon] format - swap to [lng, lat]
          lngLat = [second, first] as [number, number];
          dashLog('creating comparable marker [lat,lon]->[lng,lat]', {
            index,
            original: comp.coordinates,
            converted: lngLat
          });
        } else if (isFirstLon && isSecondLat) {
          // [lon, lat] format - already correct
          lngLat = [first, second] as [number, number];
          dashLog('creating comparable marker [lng,lat] (already correct)', {
            index,
            coordinates: lngLat
          });
        } else {
          // Fallback: assume [lat, lon] format (most common from API)
          // But log a warning for invalid coordinates
          lngLat = [second, first] as [number, number];
          dashWarn('comparable coordinates validation failed, using fallback conversion', {
            index,
            original: comp.coordinates,
            converted: lngLat,
            firstInRange: { isFirstLat, isFirstLon },
            secondInRange: { isSecondLat, isSecondLon }
          });
        }

        // Validate final coordinates are reasonable for Indonesia
        if (lngLat[0] < 95 || lngLat[0] > 141 || lngLat[1] < -11 || lngLat[1] > 5) {
          dashWarn('comparable coordinates out of Indonesia range, skipping marker', {
            index,
            address: comp.address,
            coordinates: lngLat,
            lng: lngLat[0],
            lat: lngLat[1]
          });
          return; // Skip this marker
        }

        // Create custom dot marker - using dark grey (#2D3748) with white border
        const markerElement = this.createDotMarker('#2D3748', 12);
        const marker = new mapboxgl.Marker(markerElement)
          .setLngLat(lngLat)
          .addTo(map);

        this.markers.push(marker);
        // Store the relationship between comparable and its marker
        this.comparableMarkersMap.set(comp, marker);
      } else {
        dashWarn('comparable has no coordinates', { index });
      }
    });

    dashLog('markers created', { count: this.markers.length });

    // Apply marker styles (including selection state)
    this.updateMarkerStyles();

    // Apply initial fitBounds only once when data is first loaded
    // Don't reset zoom if user has already zoomed manually
    if (!this.initialFitBoundsApplied && this.mapComponent?.mapInstance && this.polygonBounds) {
      const bounds = this.polygonBounds;
      const boundsObj = new mapboxgl.LngLatBounds(
        [bounds[0][0], bounds[0][1]],
        [bounds[1][0], bounds[1][1]]
      );

      // Wait for map to be ready
      if (map.isStyleLoaded()) {
        map.fitBounds(boundsObj, this.fitBoundsOptions);
        this.initialFitBoundsApplied = true;
        dashLog('initial map bounds applied');
      } else {
        map.once('styledata', () => {
          map.fitBounds(boundsObj, this.fitBoundsOptions);
          this.initialFitBoundsApplied = true;
          dashLog('initial map bounds applied (after style load)');
        });
      }
    }

    if (this.isPdfMode) {
      this.updatePrintMapMarkers();
    }

    dashGroupEnd();
  }

  private updatePrintMapMarkers(): void {
    if (!this.isPdfMode) {
      return;
    }

    const map = this.printMapComponent?.mapInstance;
    if (!map) {
      return;
    }

    this.clearPrintMarkers();

    // Add subject property marker as a custom dot
    if (this.subjectPropertyData?.coordinates && this.subjectPropertyData.coordinates.length === 2) {
      const [first, second] = this.subjectPropertyData.coordinates;
      let lngLat: [number, number];

      const isFirstLat = first >= -11 && first <= 5;
      const isFirstLon = first >= 95 && first <= 141;
      const isSecondLat = second >= -11 && second <= 5;
      const isSecondLon = second >= 95 && second <= 141;

      if (isFirstLat && isSecondLon) {
        lngLat = [second, first] as [number, number];
      } else if (isFirstLon && isSecondLat) {
        lngLat = [first, second] as [number, number];
      } else {
        lngLat = [second, first] as [number, number];
      }

      const markerElement = this.createDotMarker('#2D3748', 12, 'default');
      const marker = new mapboxgl.Marker(markerElement)
        .setLngLat(lngLat)
        .addTo(map);

      this.printMarkers.push(marker);
    }

    // Add comparable property markers as custom dots
    this.comparableProperties.forEach((comp, index) => {
      if (comp.coordinates && comp.coordinates.length === 2) {
        const [first, second] = comp.coordinates;
        let lngLat: [number, number];

        const isFirstLat = first >= -11 && first <= 5;
        const isFirstLon = first >= 95 && first <= 141;
        const isSecondLat = second >= -11 && second <= 5;
        const isSecondLon = second >= 95 && second <= 141;

        if (isFirstLat && isSecondLon) {
          lngLat = [second, first] as [number, number];
        } else if (isFirstLon && isSecondLat) {
          lngLat = [first, second] as [number, number];
        } else {
          lngLat = [second, first] as [number, number];
        }

        if (lngLat[0] < 95 || lngLat[0] > 141 || lngLat[1] < -11 || lngLat[1] > 5) {
          return;
        }

        const markerElement = this.createDotMarker('#2D3748', 12, 'default');
        const marker = new mapboxgl.Marker(markerElement)
          .setLngLat(lngLat)
          .addTo(map);

        this.printMarkers.push(marker);
        this.printComparableMarkersMap.set(comp, marker);
      } else {
        dashWarn('print mode: comparable has no coordinates', { index });
      }
    });
  }
}
